import { Box, Grid } from '@mui/material'

import { FetchFirmDashboardMattersQuery, useFetchFirmDashboardMattersQuery } from '../../../../generated'
import { GRAPHQL_QUERY_POLICY } from '../../../constants'
import DashboardBarChart from './DashboardBarChart'
import MatterStatusBarChart from './MatterStatusBarChart'
import SectionTitle from './SectionTitle'
import StatCard from './StatCard'
import { formatCount, formatRangeLabel, toCountBars, withRange } from './constants'

/** Tooltip shared by the matter status charts: what Total and its split cover. */
const MATTER_STATUS_NOTE =
  'Total is Active plus Completed. Active is open matters still being worked, as of today. Completed is Work Completed matters (work finished, kept open only for unpaid bills) as of today, plus matters closed within the selected period. Archived matters are not included.'

interface MattersTabProps {
  startDate: string
  endDate: string
}

const MattersTab = ({ startDate, endDate }: MattersTabProps) => {
  const { data, previousData, loading } = useFetchFirmDashboardMattersQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: { input: { startDate, endDate } }
  })

  const toMatters = (result?: FetchFirmDashboardMattersQuery) =>
    result?.fetchFirmDashboardMatters?.response?.status === 200 ? result.fetchFirmDashboardMatters : null
  const matters = toMatters(data)
  // The "as of today" figures don't depend on the range, so while a new range loads they keep showing
  // the last result instead of dropping back to a spinner and redrawing
  const snapshot = matters ?? toMatters(previousData)
  const snapshotLoading = loading && !snapshot
  const rangeLabel = formatRangeLabel(startDate, endDate)
  const statusChartSubtitle = withRange('Open matters as of today; closed matters in the period', rangeLabel)
  const count = (value?: number) => formatCount(value ?? 0)

  // Same dates as the Matter List by Attorney report: Active by date opened, Work Completed by date
  // work completed, Closed by date closed
  const statusCards = [
    {
      title: 'Active Matters',
      subtitle: 'In Period: opened in the period (a converted consult on its conversion date).',
      totals: (result: typeof matters) => result?.activeMatters
    },
    {
      title: 'Work Completed Matters',
      subtitle: 'Kept open only for unpaid bills. In Period: work completed in the period.',
      totals: (result: typeof matters) => result?.workCompletedMatters
    },
    {
      title: 'Closed Matters',
      subtitle: 'In Period: closed in the period.',
      totals: (result: typeof matters) => result?.closedMatters
    }
  ]

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <SectionTitle
        title="Matter Overview"
        subtitle="Each status as of today, and within the selected period."
      />

      <Grid container spacing={3}>
        {statusCards.map(card => (
          <Grid item xs={12} md={4} key={card.title}>
            <StatCard
              title={card.title}
              subtitle={withRange(card.subtitle, rangeLabel)}
              loading={snapshotLoading}
              items={[
                { label: 'As of Today', value: count(card.totals(snapshot)?.asOfToday) },
                // The in-period figure follows the range, so it waits for the new result
                { label: 'In Period', value: matters ? count(card.totals(matters)?.inRange) : '…' }
              ]}
            />
          </Grid>
        ))}

        <Grid item xs={12} md={6}>
          <MatterStatusBarChart
            title="Matters by Billing Type"
            subtitle={statusChartSubtitle}
            tooltip={`${MATTER_STATUS_NOTE} Contingency includes Contingency Hybrid. Other covers Pro Bono, Consult and matters with no billing type.`}
            data={matters?.activeByBillingType}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MatterStatusBarChart
            title="Matters by Law Type"
            subtitle={statusChartSubtitle}
            tooltip={MATTER_STATUS_NOTE}
            data={matters?.activeByLawType}
            loading={loading}
            orientation="horizontal"
            categoryWidth={150}
          />
        </Grid>

        <Grid item xs={12}>
          <DashboardBarChart
            title="Active Matters by Resp 1 Attorney"
            subtitle="As of today"
            tooltip="Open matters still being worked. Work Completed and Closed matters are not included."
            data={toCountBars(snapshot?.activeByResponsibleAttorney)}
            loading={snapshotLoading}
            orientation="horizontal"
            categoryWidth={170}
          />
        </Grid>
      </Grid>

      <SectionTitle title="Matter & Consult Activity" subtitle={rangeLabel} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <StatCard
            title="Matters Created"
            subtitle={withRange('A converted consult counts on its conversion date.', rangeLabel)}
            loading={loading}
            items={[
              { label: 'Total', value: count(matters?.mattersCreated.total) },
              { label: 'Directly', value: count(matters?.mattersCreated.direct) },
              { label: 'From Consults', value: count(matters?.mattersCreated.fromConsults) }
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StatCard
            title="Consults Created"
            subtitle={withRange('Consults opened in the period, and whether they have converted since.', rangeLabel)}
            loading={loading}
            items={[
              { label: 'Total', value: count(matters?.consultsCreated.total) },
              { label: 'Converted', value: count(matters?.consultsCreated.converted) },
              { label: 'Not Converted', value: count(matters?.consultsCreated.notConverted) }
            ]}
          />
        </Grid>

        <Grid item xs={12}>
          <DashboardBarChart
            title="Newly Created Matters by Billing Type"
            subtitle={rangeLabel}
            tooltip="Contingency includes Contingency Hybrid. Other covers Pro Bono, Consult and matters with no billing type."
            data={toCountBars(matters?.newMattersByBillingType)}
            loading={loading}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default MattersTab
